import React, { useCallback, useEffect, useRef, useState } from 'react';
import { API } from '@/utils/API';
import {
  FlagType,
  FLAG_LABELS,
  loadQuestionComment,
  saveQuestionComment,
  deleteQuestionComment,
  loadAllQuestionCommentsFromStorage,
  exportMarkdown,
} from '../shared/feedbackStorage';
import { FeedbackBanner } from '../shared/FeedbackBanner';
import { KIND_COLORS, templateLabel } from '../shared/formUtils';

// ─── Types ────────────────────────────────────────────────────────────────────

interface TemplateSummary {
  id: string;
  category: string;
  sub_category: string;
  variant: string;
  form_type: string;
  status: string;
  csv_id: number;
}

interface Question {
  id: string;
  slug: string;
  kind: string;
  prompt: string;
  required: boolean;
  csv_id: number;
  group_position: number;
  group_label: string;
  group_csv_id: number;
  depends_on_id: string | null;
  depends_on_csv_id: number | null;
  depends_on_prompt: string | null;
  depends_on_value: string | null;
  depends_on_op: string | null;
  submission_templates: TemplateSummary[];
}

interface Meta {
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Component ────────────────────────────────────────────────────────────────

const QuestionsIndex: React.FC = () => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [allKinds, setAllKinds] = useState<string[]>([]);
  const [allTemplates, setAllTemplates] = useState<TemplateSummary[]>([]);

  const [search, setSearch] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [templateFilter, setTemplateFilter] = useState('');
  const [selected, setSelected] = useState<Question | null>(null);

  // Navigation history: stack of csv_ids we came from
  const [navStack, setNavStack] = useState<number[]>([]);

  // Bump to force re-render of comment dots after save/delete
  const [commentVersion, setCommentVersion] = useState(0);

  const filterKey = `${search}|${kindFilter}|${templateFilter}`;
  const filterKeyRef = useRef(filterKey);
  const pageRef = useRef(1);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  // Tracks whether the current search change was triggered programmatically (nav) vs by the user
  const programmaticSearchRef = useRef(false);
  // Ref mirror of loadingPage so fetchPage doesn't need it in useCallback deps
  const loadingPageRef = useRef(false);

  // Load filter option lists once on mount
  useEffect(() => {
    API.authenticated.admin
      .fetch('/template_questions/filter_options')
      .then(r => r.json())
      .then((data: { kinds: string[]; templates: TemplateSummary[] }) => {
        setAllKinds(data.kinds);
        setAllTemplates(
          data.templates.sort((a, b) =>
            templateLabel(a).localeCompare(templateLabel(b))
          )
        );
      })
      .catch(() => {
        /* non-fatal */
      });
  }, []);

  const fetchPage = useCallback(
    (page: number, reset: boolean, autoSelectCsvId?: number) => {
      if (loadingPageRef.current) return;
      loadingPageRef.current = true;
      setLoadingPage(true);

      const params = new URLSearchParams({ page: String(page) });
      if (search) params.set('search', search);
      if (kindFilter) params.set('kind', kindFilter);
      if (templateFilter) params.set('template_id', templateFilter);

      API.authenticated.admin
        .fetch(`/template_questions?${params}`)
        .then(r => r.json())
        .then((data: { questions: Question[]; meta: Meta }) => {
          setQuestions(prev => {
            const next = reset ? data.questions : [...prev, ...data.questions];
            if (autoSelectCsvId != null) {
              const match = next.find(q => q.csv_id === autoSelectCsvId);
              if (match) setSelected(match);
            }
            return next;
          });
          setMeta(data.meta);
          pageRef.current = page;
        })
        .catch(() => setError('Failed to load questions'))
        .finally(() => {
          loadingPageRef.current = false;
          setLoadingPage(false);
          setInitialLoading(false);
        });
    },
    [search, kindFilter, templateFilter]
  );

  // Reset + fetch page 1 on filter change
  useEffect(() => {
    if (filterKey !== filterKeyRef.current) {
      filterKeyRef.current = filterKey;
      pageRef.current = 1;
      // Only clear the nav stack on user-typed changes, not programmatic navigation
      if (!programmaticSearchRef.current) {
        setSelected(null);
        setNavStack([]);
      }
      programmaticSearchRef.current = false;
    }
    setInitialLoading(true);
    setQuestions([]);
    pageRef.current = 1;
    // Block the observer from firing with stale meta during the debounce gap
    loadingPageRef.current = true;

    const id = setTimeout(
      () => {
        // Debounce window elapsed — clear the guard set above so fetchPage's
        // own loadingPageRef check (meant to prevent overlapping requests)
        // doesn't mistake it for an in-flight request and no-op forever.
        loadingPageRef.current = false;
        fetchPage(1, true);
      },
      search ? 300 : 0
    );
    return () => {
      clearTimeout(id);
      loadingPageRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  // Infinite scroll
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && meta && pageRef.current < meta.pages) {
          fetchPage(pageRef.current + 1, false);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [meta, fetchPage]);

  const hasComment = useCallback(
    (csvId: number) => loadQuestionComment(csvId) !== null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commentVersion]
  );

  // Navigate to a conditional question: push current to stack, search by csv_id
  const navigateTo = useCallback(
    (csvId: number) => {
      if (selected) {
        setNavStack(s => [...s, selected.csv_id]);
      }
      programmaticSearchRef.current = true;
      setSearch(String(csvId));
    },
    [selected]
  );

  // Navigate back
  const navigateBack = useCallback(() => {
    setNavStack(s => {
      if (s.length === 0) return s;
      const prev = s[s.length - 1];
      const next = s.slice(0, -1);
      programmaticSearchRef.current = true;
      setSearch(String(prev));
      return next;
    });
  }, []);

  const handleExport = () => {
    const comments = loadAllQuestionCommentsFromStorage();
    if (comments.length === 0) {
      alert('No feedback to export yet.');
      return;
    }
    const md = exportMarkdown('Question Feedback', comments);
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `question-feedback-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 font-sans text-sm">
      {/* Header */}
      <div className="bg-white border-b px-5 py-3 flex items-center justify-between shrink-0">
        <div>
          <div className="font-semibold text-gray-900 text-base">
            Question Browser
          </div>
          <div className="text-gray-500 text-xs mt-0.5">
            {meta ? `${meta.total} questions across all templates` : 'Loading…'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/admin"
            className="text-xs text-gray-500 hover:text-gray-800 border border-gray-200 rounded px-3 py-1.5"
          >
            ← Admin
          </a>
          <button
            onClick={handleExport}
            className="text-xs text-gray-700 border border-gray-200 rounded px-3 py-1.5 hover:bg-gray-50"
          >
            Export feedback
          </button>
          <a
            href="/admin_workflows/submission_templates"
            className="text-xs bg-gray-900 text-white rounded px-3 py-1.5 hover:bg-gray-700"
          >
            Browse Templates
          </a>
        </div>
      </div>

      <FeedbackBanner />

      {/* Filters */}
      <div className="bg-white border-b px-5 py-2.5 flex gap-2 shrink-0">
        {navStack.length > 0 && (
          <button
            onClick={navigateBack}
            className="text-xs text-blue-600 hover:text-blue-800 border border-blue-200 rounded px-2.5 py-1.5 shrink-0"
          >
            ← Q{navStack[navStack.length - 1]}
          </button>
        )}
        <input
          type="text"
          placeholder="Search by prompt, Q#, group, or slug…"
          value={search}
          onChange={e => {
            programmaticSearchRef.current = false;
            setSearch(e.target.value);
            setNavStack([]);
          }}
          className="flex-1 border rounded px-2.5 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
        />
        <select
          value={kindFilter}
          onChange={e => setKindFilter(e.target.value)}
          className="border rounded px-2.5 py-1.5 text-xs text-gray-600 bg-white"
        >
          <option value="">All kinds</option>
          {allKinds.map(k => (
            <option key={k} value={k}>
              {k}
            </option>
          ))}
        </select>
        <select
          value={templateFilter}
          onChange={e => setTemplateFilter(e.target.value)}
          className="border rounded px-2.5 py-1.5 text-xs text-gray-600 bg-white max-w-xs"
        >
          <option value="">All templates</option>
          {allTemplates.map(t => (
            <option key={t.id} value={t.id}>
              {templateLabel(t)}
            </option>
          ))}
        </select>
        {(search || kindFilter || templateFilter) && (
          <button
            onClick={() => {
              setSearch('');
              setKindFilter('');
              setTemplateFilter('');
              setNavStack([]);
            }}
            className="text-xs text-gray-400 hover:text-gray-600 px-1"
          >
            Clear
          </button>
        )}
      </div>

      <div className="text-xs text-gray-400 px-5 py-1.5 bg-white border-b shrink-0">
        {meta
          ? `Showing ${questions.length} of ${meta.total} questions`
          : initialLoading
            ? 'Loading…'
            : ''}
      </div>

      {error && (
        <div className="text-red-500 text-xs text-center py-4">{error}</div>
      )}

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: list */}
        <div ref={listRef} className="w-1/2 overflow-y-auto border-r bg-white">
          {initialLoading && questions.length === 0 ? (
            <div className="text-gray-400 text-xs text-center py-16">
              Loading questions…
            </div>
          ) : (
            <>
              {questions.map(q => {
                const isSelected = selected?.id === q.id;
                const commented = q.csv_id != null && hasComment(q.csv_id);
                return (
                  <button
                    key={q.id}
                    onClick={() => setSelected(q)}
                    className={`w-full text-left px-4 py-2.5 border-b flex items-start gap-2 hover:bg-blue-50 transition-colors ${
                      isSelected
                        ? 'bg-blue-50 border-l-2 border-l-blue-500'
                        : ''
                    } ${commented && !isSelected ? 'bg-yellow-50' : ''}`}
                  >
                    <span className="text-gray-400 text-xs font-mono shrink-0 mt-0.5 w-12">
                      Q{q.csv_id}
                    </span>
                    <span className="flex-1 text-gray-800 text-xs leading-snug">
                      {q.prompt.length > 90
                        ? q.prompt.slice(0, 90) + '…'
                        : q.prompt}
                      <span className="text-gray-400 text-xs block mt-0.5">
                        {q.group_label}
                      </span>
                    </span>
                    <div className="flex flex-col items-end gap-1 shrink-0">
                      <span
                        className={`text-xs rounded px-1.5 py-0.5 font-mono ${KIND_COLORS[q.kind] ?? 'bg-gray-100 text-gray-700'}`}
                      >
                        {q.kind}
                      </span>
                      <span className="text-gray-400 text-xs">
                        {q.submission_templates.length}t
                      </span>
                    </div>
                    {commented && (
                      <span
                        className="w-2 h-2 rounded-full bg-yellow-400 shrink-0 mt-1"
                        title="Has feedback"
                      />
                    )}
                  </button>
                );
              })}

              <div ref={sentinelRef} className="py-4 text-center">
                {loadingPage && (
                  <span className="text-gray-400 text-xs">Loading more…</span>
                )}
                {!loadingPage &&
                  meta &&
                  questions.length >= meta.total &&
                  meta.total > 0 && (
                    <span className="text-gray-300 text-xs">
                      All {meta.total} questions loaded
                    </span>
                  )}
              </div>

              {!initialLoading && questions.length === 0 && (
                <div className="text-gray-400 text-xs text-center py-16">
                  No questions match the current filters.
                </div>
              )}
            </>
          )}
        </div>

        {/* Right: detail */}
        <div className="w-1/2 overflow-y-auto p-5">
          {selected ? (
            <QuestionDetail
              q={selected}
              commentVersion={commentVersion}
              onCommentChange={() => setCommentVersion(v => v + 1)}
              onNavigateTo={navigateTo}
            />
          ) : (
            <div className="text-gray-400 mt-12 text-center">
              Select a question to see details and add feedback
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Question Detail ──────────────────────────────────────────────────────────

const QuestionDetail: React.FC<{
  q: Question;
  commentVersion: number;
  onCommentChange: () => void;
  onNavigateTo: (csvId: number) => void;
}> = ({ q, commentVersion, onCommentChange, onNavigateTo }) => {
  const existing = q.csv_id != null ? loadQuestionComment(q.csv_id) : null;
  const [commentText, setCommentText] = useState(existing?.text ?? '');
  const [flagType, setFlagType] = useState<FlagType>(
    existing?.flagType ?? 'other'
  );
  const [dependents, setDependents] = useState<Question[]>([]);

  useEffect(() => {
    const c = q.csv_id != null ? loadQuestionComment(q.csv_id) : null;
    setCommentText(c?.text ?? '');
    setFlagType(c?.flagType ?? 'other');
  }, [q.id, q.csv_id, commentVersion]);

  useEffect(() => {
    setDependents([]);
    API.authenticated.admin
      .fetch(`/template_questions?depends_on_id=${q.id}`)
      .then(r => r.json())
      .then((data: { questions: Question[] }) => setDependents(data.questions))
      .catch(() => {
        /* non-fatal */
      });
  }, [q.id]);

  const handleSave = () => {
    if (!commentText.trim() || q.csv_id == null) return;
    saveQuestionComment({
      csvId: q.csv_id,
      slug: q.slug,
      prompt: q.prompt,
      groupLabel: q.group_label ?? '',
      flagType,
      text: commentText,
      createdAt: new Date().toISOString(),
    });
    onCommentChange();
  };

  const handleDelete = () => {
    if (q.csv_id == null) return;
    deleteQuestionComment(q.csv_id);
    setCommentText('');
    setFlagType('other');
    onCommentChange();
  };

  const hasExisting =
    q.csv_id != null && loadQuestionComment(q.csv_id) !== null;

  // Deduplicate dependents by prompt
  const dependentGroups = (() => {
    const groups = new Map<
      string,
      {
        kinds: Set<string>;
        whenValues: Set<string>;
        csvIds: number[];
        firstQuestion: Question;
      }
    >();
    for (const d of dependents) {
      const key = d.prompt.trim();
      if (!groups.has(key))
        groups.set(key, {
          kinds: new Set(),
          whenValues: new Set(),
          csvIds: [],
          firstQuestion: d,
        });
      const g = groups.get(key)!;
      g.kinds.add(d.kind);
      // Key by operator too: a dependent shown when = "X" and one shown when != "X"
      // are opposite conditions and must not collapse into a single chip.
      if (d.depends_on_value)
        g.whenValues.add(`${d.depends_on_op ?? '='} "${d.depends_on_value}"`);
      g.csvIds.push(d.csv_id);
    }
    return Array.from(groups.entries());
  })();

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono text-sm font-semibold text-gray-700">
            Q{q.csv_id}
          </span>
          <span
            className={`text-xs rounded px-1.5 py-0.5 font-mono ${KIND_COLORS[q.kind] ?? 'bg-gray-100 text-gray-700'}`}
          >
            {q.kind}
          </span>
          {q.required && <span className="text-xs text-red-600">required</span>}
        </div>
        <div className="text-gray-400 text-xs">{q.group_label}</div>
      </div>

      <div className="bg-gray-50 rounded p-3 text-gray-800 text-sm leading-relaxed">
        {q.prompt}
      </div>

      {q.depends_on_id && (
        <div className="text-xs text-gray-500 bg-gray-50 rounded p-2.5 border border-gray-100">
          <span className="font-medium text-gray-400 block mb-1">
            {q.depends_on_op === '!=' ? 'Shown unless' : 'Shown when'}
          </span>
          {q.depends_on_csv_id != null ? (
            <button
              onClick={() => onNavigateTo(q.depends_on_csv_id!)}
              className="text-blue-500 hover:text-blue-700 text-left leading-snug"
            >
              {q.depends_on_prompt ?? `Q${q.depends_on_csv_id}`}
            </button>
          ) : (
            <span className="leading-snug">
              {q.depends_on_prompt ?? 'parent question'}
            </span>
          )}
          <span className="block mt-1 text-gray-500">
            {q.depends_on_op ?? '='} &quot;{q.depends_on_value}&quot;
          </span>
        </div>
      )}

      <div className="text-xs text-gray-400 font-mono">{q.slug}</div>

      {/* Templates */}
      {q.submission_templates.length > 0 && (
        <div className="border-t pt-4">
          <div className="text-xs font-semibold text-gray-700 mb-2">
            Appears on {q.submission_templates.length} template
            {q.submission_templates.length !== 1 ? 's' : ''}
          </div>
          <div className="space-y-1">
            {q.submission_templates.map(t => (
              <div
                key={t.id}
                className="text-xs text-gray-600 bg-gray-50 rounded px-2.5 py-1.5 border border-gray-100"
              >
                {templateLabel(t)}
                <span className="text-gray-400 ml-1">· {t.form_type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Conditional questions */}
      {dependentGroups.length > 0 && (
        <div className="border-t pt-4">
          <div className="text-xs font-semibold text-gray-700 mb-1">
            Conditional questions
          </div>
          <div className="text-xs text-gray-400 mb-2">
            {dependentGroups.length} unique prompt
            {dependentGroups.length !== 1 ? 's' : ''} · {dependents.length}{' '}
            total instances
          </div>
          <div className="space-y-1.5">
            {dependentGroups.map(([prompt, g]) => (
              <button
                key={prompt}
                onClick={() => onNavigateTo(g.csvIds[0])}
                className="w-full text-left text-xs bg-gray-50 border border-gray-100 rounded px-2.5 py-2 hover:bg-blue-50 hover:border-blue-200 transition-colors group"
              >
                <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                  {Array.from(g.kinds).map(k => (
                    <span
                      key={k}
                      className={`rounded px-1 py-0.5 font-mono ${KIND_COLORS[k] ?? 'bg-gray-100 text-gray-700'}`}
                    >
                      {k}
                    </span>
                  ))}
                  {Array.from(g.whenValues).map(v => (
                    <span key={v} className="text-gray-400">
                      when {v}
                    </span>
                  ))}
                  <span className="ml-auto text-blue-400 opacity-0 group-hover:opacity-100 text-xs">
                    Q{g.csvIds[0]} →
                  </span>
                  {g.csvIds.length > 1 && (
                    <span className="text-gray-300 font-mono">
                      {g.csvIds.length}×
                    </span>
                  )}
                </div>
                <div className="text-gray-700 leading-snug">{prompt}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Feedback form */}
      <div className="border-t pt-4 space-y-3">
        <div className="text-xs font-semibold text-gray-700">
          {hasExisting ? 'Edit feedback' : 'Add feedback'}
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">Issue type</label>
          <select
            value={flagType}
            onChange={e => setFlagType(e.target.value as FlagType)}
            className="w-full border rounded px-2 py-1.5 text-xs text-gray-700 bg-white"
          >
            {(Object.entries(FLAG_LABELS) as [FlagType, string][]).map(
              ([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              )
            )}
          </select>
        </div>

        <div>
          <label className="text-xs text-gray-500 block mb-1">
            Description
          </label>
          <textarea
            value={commentText}
            onChange={e => setCommentText(e.target.value)}
            placeholder="Describe the issue…"
            rows={4}
            className="w-full border rounded px-2 py-1.5 text-xs text-gray-700 resize-none focus:outline-none focus:ring-1 focus:ring-blue-400"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={!commentText.trim()}
            className="text-xs bg-blue-600 text-white rounded px-3 py-1.5 hover:bg-blue-700 disabled:opacity-40"
          >
            {hasExisting ? 'Update' : 'Save'}
          </button>
          {hasExisting && (
            <button
              onClick={handleDelete}
              className="text-xs text-red-600 hover:text-red-800 px-2"
            >
              Remove
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionsIndex;
